type ObjectField = {
  key: string,
  value?: string,
  isArray?: boolean
}

type MappingObject = {
  [key: string]: ObjectField | MappingObject
}

type MappingArray = {
  key?: string,
  mapKey: ObjectField,
  subKeys?: MappingArray[]
}

type ObjectToArrayOptions = {
  mapping: MappingObject | ObjectField
}

type ArrayToObjectOptions = {
  mapping: MappingArray
}

interface ReadableObjectArray {
  key: string
  value: string | number | boolean | null | ReadableObjectArray[]
}

/**
 * Custom guards
 */
const isObjectField = (mapping: any): mapping is ObjectField => mapping.key


/** 
 * Helpers
 */

const nestedObjectToArray = (obj: any, mapping: MappingObject, index = 0): ReadableObjectArray[] => {
  if (typeof obj !== 'object' || !obj) return obj

  const mappingKeys = Object.keys(mapping)

  return Object.keys(obj).map(key => {
    const field = obj[key]

    let mapped: any = {}
    if (typeof field === 'object') {
      const mapKey = mappingKeys[index] || 'key'

      const nestedMapping = mapping[mapKey] as any
      const nestedKey = nestedMapping ? (Object.keys(nestedMapping)[0] as string) || 'nested' : 'nested'

      mapped = {
        ...mapped,
        [mapKey]: key,
        [nestedKey]: nestedObjectToArray(field, nestedMapping[nestedKey] as MappingObject, index + 1)
      }
    } else {
      const currentMapping = isObjectField(mapping) ? mapping : mapping[0]
      const mappedKey = currentMapping?.key as string || 'key'
      const mappedValue = currentMapping?.value as string || 'value'

      if (currentMapping?.isArray) {
        mapped = {
          ...mapped,
          ...{
            [mappedKey]: field,
          }
        }
      } else {
        mapped = {
          ...mapped,
          ...{
            [mappedKey]: key,
            [mappedValue]: field
          }
        }
      }
    }

    return mapped
  })
}

const simpleObjectToArray = (input: any, mapping: ObjectField) => {
  return Object.keys(input).map(fieldKey => {
    const mapKey = mapping.key
    const mapValue = mapping.value || 'value'

    const value = input[fieldKey] || undefined

    return {
      [mapKey]: fieldKey,
      [mapValue]: value
    }
  })
}

const nestedArrayToObject = (input: any, mapping: MappingArray) => {
  if (!input) return null

  if (input instanceof Array) {
    if (input.length === 0) return null

    const mapKey = mapping.mapKey.key

    const result: Record<any, any> = {}
    input.forEach(obj => {
      const fieldKey = obj[mapKey]
      
      let value: Record<any, any> | any[] | null = null
      mapping.subKeys?.forEach((subMapKey) => {
        const key = subMapKey.key || 'key'

        if (subMapKey.mapKey.isArray) {
          value = simpleArrayToObject(obj[key], subMapKey)
        } else {
          value = {
            ...value,
            ...simpleArrayToObject(obj[key], subMapKey)
          }
        }
      })

      result[fieldKey] = value
    })

    return result
  }

  return null
}

const simpleArrayToObject = (input: any, mapping: MappingArray) => {
  if (!input) return null

  if (input instanceof Array) {
    if (mapping.mapKey.key.length > 0) {
      const mapKey = mapping.mapKey.key
      const mapValue = mapping.mapKey.value || 'value'

      // We should output this as array :)
      if (mapping.mapKey.isArray) {
        const result: any[] = []
        input.forEach(obj => {
          // In this context our key will be our value
          const value = obj[mapKey]

          if (typeof value !== 'undefined') {
            result.push(value)
          }
        })

        return result
      } else {
        const result: Record<any, any> = {}
        input.forEach(obj => {
          const key = obj[mapKey]
          let value = obj[mapValue]
  
          // Fix undefined values
          value = typeof value !== 'undefined' ? value : null
  
          if (!value) {
            result[key] = value
          } else {
            result[key] = isNaN(value) ? value : Number(value)
          }
        })
  
        return result
      }
    } else { // Only value format
      const mapValue = mapping.mapKey.value || 'value'

      const result: any[] = []
      input.forEach(field => {
        let value = field[mapValue]

        if (value) {
          value = isNaN(value) ? value : Number(value)
          result.push(value)
        }
      })

      return result
    }
  }

  return null
}

/**
 * Parse object to array
 * 
 * @param input 
 * @param options 
 * @returns 
 */
export const normalizeToArray = (input: any, options?: ObjectToArrayOptions) => {
  if (!input) return []

  let mapping = options?.mapping

  // Preserve array
  if (input instanceof Array) {
    return input.map(value => {
      return {
        value
      }
    })
  }

  // If we are dealing with object and we do not have a mapping
  // So we should at least try to map with default values :)
  if (!mapping) {
    mapping = {
      key: 'map_type'
    } as ObjectField
  }

  if (isObjectField(mapping)) {
    return simpleObjectToArray(input, mapping)
  }

  return nestedObjectToArray(input, mapping)
}

/**
 * Parse array to object
 * 
 * @param input 
 * @param options 
 * @returns 
 */
export const normalizeToObject = (input: any, options?: ArrayToObjectOptions) => {
  let mapping = options?.mapping

  if (!mapping) {
    mapping = {
      mapKey: {
        key: 'map_type'
      }
    }
  }

  if (mapping.subKeys) {
    return nestedArrayToObject(input, mapping)
  } else {
    return simpleArrayToObject(input, mapping)
  }
}


export const normalizeDynReceiptMapToObject = (input: any[]) => {
  const filtered = input?.filter(v => v != null && v?.map_type != null)
  filtered?.forEach(el => {
    el.value?.forEach(it => {
      it.value = isNaN(it.value) ? it.value : parseInt(it.value)
    })
  })
  const entries = normalizeToObject(filtered)
  return filtered?.length > 0 && entries ? { entries } : {} // {} at top level => remove key
}
